'use client';

import { CustomQueryStringParameterItems, Sys } from 'libs/contentful/types';
import { cn } from 'utils/styles';
import { usePage } from 'contexts/PageProvider';
import { getButtonStyles } from 'components/shared/Buttons/BaseButton';
import {
  applyEnrollmentParams,
  constructEnrollmentUrlWithQueryParams,
  getCTAType,
  getLinkType,
  navigateTo,
  type EnrollmentUrlContext,
} from 'utils/linkHelpers';
import { EMAIL_CAPTURE_COOKIE, createCookieOptions } from 'utils/cookies';
import { hasConsent, ONETRUST_CATEGORIES } from 'libs/oneTrust';
import { SubmitHandler, useForm } from 'react-hook-form';
import { setCookie } from 'cookies-next';
import { useAnalyticsModuleTracking } from 'components/analytics/AnalyticsModuleTracking';
import { useLandingPageEmailTracking, useLeadGenerationInteraction } from 'components/analytics/hooks';
import { convertObjectToArray } from 'components/analytics/utils';
import { EMAIL_REGEX } from 'utils/formatters';
import { useEffect, useRef, useState } from 'react';
import Input from '../Input/Input';
import BaseLink from 'components/shared/Link/BaseLink';
import { trackLandingPageEmailFilled, trackEnrollmentCtaTapped } from 'components/analytics/eventHandlerTracking';
import { LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { addSpeculationRule } from '../../../utils/getSpeculationRules';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { convertCustomQspsToObject } from 'utils/formatters';
import { getCustomQueryStringParametersItemsForClient } from 'utils/pageHelpers/customQueryStringParametersClient';
import { PageContextValue } from 'contexts/pageContextValue';

export interface EmailCaptureProps {
  formClassName?: string;
  buttonClassName?: string;
  buttonText: string;
  sys?: Sys;
  appliedPromoCode?: string;
  baseEnrollmentUrl: string;
  // Override the live-preview target for the submit button. Default points at
  // the parent entry's `emailInputButtonText` field, which exists on hero-style
  // content types. Callers whose button text lives on a different entry/field
  // (e.g. a linked Cta) should pass an explicit override.
  buttonLiveUpdate?: { entryId?: string; fieldId?: string };
}

interface FormData {
  email: string;
}

const constructEnrollmentUrl = (
  page: PageContextValue | undefined,
  baseEnrollmentUrl: string,
  appliedPromoCode?: string,
  customQspsOverride?: CustomQueryStringParameterItems[] | null,
  enrollmentUrlContext?: EnrollmentUrlContext,
): string => {
  const additionalParams = appliedPromoCode
    ? {
        promo: appliedPromoCode.toLowerCase(),
        ad: `dmail_${appliedPromoCode.toLowerCase()}`,
      }
    : undefined;

  const customItems = Array.isArray(customQspsOverride)
    ? customQspsOverride
    : getCustomQueryStringParametersItemsForClient(page);
  const customParams = convertCustomQspsToObject(customItems);

  const allParams = {
    ...additionalParams,
    ...customParams,
  };

  return constructEnrollmentUrlWithQueryParams(baseEnrollmentUrl, allParams, enrollmentUrlContext);
};

const EmailCapture = ({
  formClassName,
  buttonClassName,
  sys,
  buttonText,
  appliedPromoCode,
  baseEnrollmentUrl,
  buttonLiveUpdate,
}: EmailCaptureProps) => {
  const { page, enrollmentUrlContext, resolvedCustomQueryStringParametersOverride } = usePage();
  const customQsps = Array.isArray(resolvedCustomQueryStringParametersOverride)
    ? resolvedCustomQueryStringParametersOverride
    : undefined;
  const enrollmentUrl = constructEnrollmentUrl(
    page,
    baseEnrollmentUrl,
    appliedPromoCode,
    customQsps,
    enrollmentUrlContext,
  );
  const [submitHref, setSubmitHref] = useState(enrollmentUrl);
  // Tracked via a ref (not state) so the fire-once guard is read synchronously —
  // a blur followed by a submit in the same tick must not double-fire.
  const landingInteractionCaptured = useRef(false);

  useEffect(() => {
    const decoratedHref = applyEnrollmentParams(enrollmentUrl);
    setSubmitHref(decoratedHref);
    return addSpeculationRule(decoratedHref);
  }, [enrollmentUrl]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onBlur', defaultValues: { email: '' } });

  // Destructure the register result to preserve react-hook-form's onBlur
  const {
    onBlur: registerOnBlur,
    ref: registerRef,
    ...registerRest
  } = register('email', {
    pattern: EMAIL_REGEX,
    required: false,
  });

  const { attributes } = useAnalyticsModuleTracking();
  const interactionType = LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED;

  const analyticsProps = {
    interaction_type: interactionType,
    button_location: attributes.moduleType,
    interaction_details: {
      location: enrollmentUrl,
      action_detail: buttonText,
    },
    email_entered: false,
    ignored_qsps: {},
    user_alias: {
      alias_label: 'pre_enrollment',
      alias_name: {},
    },
    additional_data: convertObjectToArray({
      destination: enrollmentUrl,
      label: buttonText,
      button_name: buttonText,
      cta_type: getCTAType(interactionType, true),
      link_type: getLinkType(enrollmentUrl),
    }),
  };

  const trackLeadGenEnrollmentCTATapped = useLeadGenerationInteraction(analyticsProps);
  const trackLandingPageEmailInteraction = useLandingPageEmailTracking();

  // Fire the landing page email event once the user has committed an email — on
  // blur, or on submit if the field never blurred (e.g. the user pressed Enter /
  // a mobile keyboard action that keeps focus). The EMAIL_REGEX guard is what
  // keeps partial addresses out (a 2+ char TLD), not the timing — firing on blur
  // rather than per keystroke just avoids capturing mid-typing values like
  // "@chime.c".
  const captureLandingInteraction = (email: string) => {
    if (landingInteractionCaptured.current || !EMAIL_REGEX.test(email)) return;

    trackLandingPageEmailInteraction();
    trackLandingPageEmailFilled({
      email,
      button_location: attributes.moduleType,
    });
    landingInteractionCaptured.current = true;
  };

  const onSubmit: SubmitHandler<FormData> = data => {
    if (!enrollmentUrl) return;
    const email = data.email;

    // Capture the landing page email here too, in case the field was never
    // blurred before submitting (Enter key / mobile keyboard). Idempotent.
    captureLandingInteraction(email);

    if (email && hasConsent(ONETRUST_CATEGORIES.FUNCTIONAL)) {
      setCookie(EMAIL_CAPTURE_COOKIE, email, createCookieOptions({ enableCrossSubdomain: true }));
    }

    const finalUrl = applyEnrollmentParams(enrollmentUrl);

    try {
      trackEnrollmentCtaTapped({
        label: buttonText,
        item_value: enrollmentUrl,
        module: {
          module_title: attributes.moduleTitle,
          module_name: attributes.moduleName,
          module_index: attributes.moduleIndex,
          module_type: attributes.moduleType,
        },
        cta_type: getCTAType(interactionType, true),
        button_location: attributes.moduleType,
      });

      trackLeadGenEnrollmentCTATapped({ email: email, email_entered: !!email });
    } catch (err) {
      // Tracking can throw synchronously in Firefox under strict referrer
      // policy. Don't block the redirect on a tracking failure.
      console.error('Enrollment tracking failed', err);
    }

    navigateTo(finalUrl);
  };

  const handleCtaClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isModifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

    if (isModifiedClick) return;

    event.preventDefault();
    void handleSubmit(onSubmit)();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Call react-hook-form's onBlur first to trigger validation on this input
    registerOnBlur(e);

    // Capture the landing page email when the user leaves the field (the guard in
    // captureLandingInteraction only fires for a complete, valid address).
    captureLandingInteraction(e.target.value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="email-capture-form">
      <div className={cn('flex-col flex items-end gap-[10px] mb-0 h-full', 'md:flex-row w-full', formClassName)}>
        <Input
          label="Enter your email"
          {...registerRest}
          ref={registerRef}
          error={'Please enter a valid email address.'}
          invalid={!!errors.email}
          onBlur={handleBlur}
        />
        <BaseLink
          asButton
          href={submitHref}
          onClick={handleCtaClick}
          className={getButtonStyles('solid', buttonClassName)}
          {...liveUpdateAttrs(
            buttonLiveUpdate?.entryId ?? sys?.id,
            buttonLiveUpdate?.fieldId ?? 'emailInputButtonText',
          )}>
          {buttonText}
        </BaseLink>
      </div>
    </form>
  );
};

export default EmailCapture;
